import React, { useState, useEffect } from "react";
import { isEmpty } from "lodash";
import moment from "moment";
import { connect } from "react-redux";
import mixpanel from "mixpanel-browser";

import { MODEL_COLL } from "_firebase";

import { required, maxLength, getValidation } from "utils/fieldValidation";
import { getProgramType } from "utils/evaluationProgram";
import getModelTemplates from "utils/getModelTemplates";
import { getOptions, getFirstOption } from "utils/fields";
import { useStateSelector } from "utils/field";
import { getDefaultF280Option } from "utils/weather";
import { applyImportedRoomMigrationIfNeeded } from "utils/roomByRoom";

import { actions as modelActions } from "features/Model/_ducks";
import { actions as resultsActions } from "features/Model/Review/Results/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { actions as userActions } from "store/users";
import { clearDrawingData } from "features/Model/DrawingNew/_ducks/actions";
import { change } from "store/root/actions";
import { resetForm } from "store/form/actions";

import Select from "components/Input/Select";
import Button from "components/Button";
import DayPicker from "components/Input/DayPicker";
import Input from "components/Input";
import InputRow from "components/Input/Row";
import InputFileDragAndDrop from "components/Input/FileDragAndDropUpload";
import SelectFolder from "components/SelectModelFolder";
import Field from "components/Field";

import classes from "../style.module.scss";

const charLim100 = maxLength(100);

const { fetchModelData, createModel, modelLoading, setExportErrors, clearModelData } = modelActions;
const { resetResults } = resultsActions;
const { clearUpgradeData } = upgradeActions;
const { fetchUserDir } = userActions;

const templates = [
    {
        value: "default-v11.13",
        label: "None (HOT2000 v11.13)",
    },
    {
        value: "default-v11.12",
        label: "None (HOT2000 v11.12)",
    },
    {
        value: "default-v11.11",
        label: "None (HOT2000 v11.11)",
    },
    {
        value: "default-v11.10",
        label: "None (HOT2000 v11.10)",
    },
    {
        value: "default-v11.9",
        label: "None (HOT2000 v11.9)",
    },
];

const Basic = ({
    change,
    invalid,
    history,
    clearModelData,
    clearDrawingData,
    resetResults,
    clearUpgradeData,
    setExportErrors,
    modelLoading,
    fetchModelData,
    fetchUserDir,
    createModel,
    resetForm,
    hasFiveModels,
    currentFolderId,
    setCurrentFolderId,
}) => {
    const [isSelectingFolderOpen, setIsSelectingFolderOpen] = useState(false);
    const [versionError, setVersionError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const {
        units = "Metric",
        modelDir: { folders = {} } = {},
        uid,
        name: username,
        userMeta: { company = "" } = {},
        userPreferences: {
            userPhone = "",
            eaId = "",
            companyPhone = "",
            province: { id: provinceId } = {},
            preferredUnits = "",
            preferredProgramType = "",
        } = {},
    } = useStateSelector("user");

    const selectedTemplate = useStateSelector("newForm.create.template") || "default-v11.13";
    const form = useStateSelector("newForm.create");

    useEffect(() => {
        let defaultTemplate = getModelTemplates(selectedTemplate);
        defaultTemplate =
            typeof structuredClone === "function"
                ? structuredClone(defaultTemplate)
                : JSON.parse(JSON.stringify(defaultTemplate));

        defaultTemplate.general.file.evaluationDate = moment().format("YYYY-MM-DD");
        defaultTemplate.general.file.userNameEnteredBy = username;
        defaultTemplate.general.file.userTelephone = userPhone;
        defaultTemplate.general.file.companyTelephone = companyPhone;
        defaultTemplate.general.file.userCompany = company;
        defaultTemplate.general.file.fileID = eaId;

        defaultTemplate.program.class = preferredProgramType;
        defaultTemplate.uiSettings.primaryUnits = preferredUnits || units;

        if (provinceId != null) {
            const provinceOpts = getOptions({
                fieldKey: "weatherRegion",
                version: "v11-11",
            });
            const selectedProvTerr = provinceOpts.find((el) => el.value.id === provinceId) || {};

            defaultTemplate.general.client.clientProvince = selectedProvTerr.label;

            defaultTemplate.location.weather.region = { id: provinceId };
            const locObj = getFirstOption({
                indKey: provinceId,
                depKey: "weatherLocation",
                version: "v11-11",
            });

            defaultTemplate.location.weather.location = locObj;
            defaultTemplate.location.weather.f280.location.id = getDefaultF280Option(locObj.id);
        }

        change("newForm.create", {
            owner: {
                uid,
                name: username,
            },
            template: selectedTemplate,
            modelData: defaultTemplate,
        });
    }, [
        uid,
        username,
        selectedTemplate,
        userPhone,
        companyPhone,
        company,
        eaId,
        preferredProgramType,
        preferredUnits,
        units,
        provinceId,
    ]);

    useEffect(() => {
        fetchUserDir(uid);
    }, []);

    change("newForm.create.parentFolderId", currentFolderId);

    const handleUpload = (data) => {
        if (isEmpty(data)) {
            return;
        }

        const {
            modelData = {},
            modelDetails = {},
            modelPath = "",
            version: { major = 11, minor = 9 },
        } = data || {};
        const { name = "" } = modelDetails;
        const modelId = modelPath.split(`${MODEL_COLL}/`)[1];

        if (name !== "") {
            //Handles setting the name on import
            change("newForm.create.name", name);
        }

        applyImportedRoomMigrationIfNeeded(modelData);

        change("newForm.create.modelDetails", modelDetails);
        change("newForm.create.modelData", modelData);
        change("newForm.create.modelId", modelId);
        change("newForm.create.template", "");

        if (minor < 9 || major < 11) {
            setVersionError(`${major}.${minor}`);
        }

        mixpanel.track("H2K Import");
    };

    const handleProgramTypeChange = (value) => {
        const programType = getProgramType(value);

        change("newForm.create.modelData.program.type", programType);
    };

    const handleSubmit = async () => {
        setSubmitting(true);

        const {
            modelId: generatedId = "",
            modelData: { uiSettings: { primaryUnits } = {} } = {},
            parentFolderId = null,
        } = form;

        const modelId =
            generatedId ||
            (await createModel(
                {
                    ...form,
                    parentFolderId,
                    createdAt: moment().format(),
                    sharedWith: [uid],
                },
                uid,
                parentFolderId
            ).then((id) => id));

        await clearModelData();
        await clearDrawingData();
        await resetResults();
        await clearUpgradeData();
        await setExportErrors([]);
        await modelLoading();
        await fetchModelData(modelId);

        mixpanel.track("Create Model", {
            "Primary Units": primaryUnits,
        });
        setSubmitting(false);

        resetForm("create");

        return history.push(`/models/${modelId}`);
    };

    const canCreateModel = hasFiveModels || submitting || (form?.name ? form.name?.length <= 0 : true);

    return (
        <div className={classes.createForm}>
            <div className={classes.sectionHeader}>
                <h3>Create a Model from Scratch</h3>
            </div>
            <InputRow>
                <Field
                    component={Input}
                    name="newForm.create.name"
                    type="text"
                    label="Name*"
                    placeholder="Name Your Model"
                    validate={[required, charLim100]}
                    addError={false}
                />
            </InputRow>

            <InputRow gridTemplate="1fr 1fr">
                <Field
                    component={DayPicker}
                    formatting="YYYY-MM-DD"
                    name="newForm.create.modelData.general.file.evaluationDate"
                    label="Evaluation Date*"
                    validate={getValidation("evaluationDate")}
                    addError={false}
                />
                <Field
                    component={Select}
                    name="newForm.create.modelData.uiSettings.primaryUnits"
                    options={[
                        {
                            value: "Metric",
                            label: "Metric",
                        },
                        {
                            value: "Imperial",
                            label: "Imperial",
                        },
                        {
                            value: "US",
                            label: "US",
                        },
                    ]}
                    label="Primary Display Units*"
                    placeholder="Choose Display Units"
                />
            </InputRow>
            <InputRow
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "20px",
                    fontSize: "0.875rem",
                }}
            >
                <p>
                    Current folder -{" "}
                    <strong>
                        {
                            currentFolderId && folders && folders[currentFolderId]?.name // check if currentFolderId exists and folders is not empty
                                ? folders[currentFolderId].name // if currentFolderId exists, use the folder name
                                : currentFolderId && folders && !folders[currentFolderId]?.name // if currentFolderId exists but folder name is not set
                                ? "Unknown Folder" // if folder name is not set
                                : "Model Directory" // if currentFolderId is not set or folders is empty, use "Model Directory"
                        }
                    </strong>
                </p>
                <p
                    style={{
                        cursor: "pointer",
                        color: "#0049c6",
                        fontWeight: 500,
                    }}
                    className={classes.folderText}
                    onClick={() => setIsSelectingFolderOpen(!isSelectingFolderOpen)}
                >
                    Select Folder
                </p>
            </InputRow>
            <InputRow>
                <Field
                    component={Select}
                    name="newForm.create.modelData.program.class"
                    options={[
                        {
                            value: "",
                            label: "General",
                        },
                        {
                            value: "ca.nrcan.gc.OEE.ERS.ErsProgram",
                            label: "EnerGuide Rating System",
                        },
                        {
                            value: "ca.nrcan.gc.OEE.ERS2020NBC.ErsProgram",
                            label: "EnerGuide Rating System 2020 NBC",
                        },
                        {
                            value: "ca.nrcan.gc.OEE.ONrh.OnProgram",
                            label: "Ontario Reference House",
                        },
                    ]}
                    label="Program Type"
                    placeholder="None"
                    onChange={handleProgramTypeChange}
                />
            </InputRow>
            <div className={classes.divider}></div>
            <h3>Upload a HOT2000 File (Optional)</h3>
            <InputRow className={classes.lastRow}>
                <Field
                    component={InputFileDragAndDrop}
                    name="newForm.create.H2kFileInput"
                    id="H2kFileInput"
                    text="Working from a file newer than v11.9?"
                    handleUpload={handleUpload}
                    accept=".h2k, .xml"
                    parentFolderId={currentFolderId}
                />
            </InputRow>

            {versionError && (
                <h5
                    style={{ color: "#c61717", marginTop: "1rem" }}
                >{`The file uploaded was created using HOT2000 version ${versionError}, which is not supported. We have attempted to update it to 11.9 but this may produce calculation errors. It is recommended that you first open and save the file in a version of HOT2000 newer than 11.9 prior to upload.`}</h5>
            )}
            <Button large className={classes.createButton} onClick={() => handleSubmit()} disabled={canCreateModel}>
                {submitting ? "Creating Model..." : "Create"}
            </Button>
            <SelectFolder
                folderToMove={isSelectingFolderOpen}
                folders={folders}
                close={() => setIsSelectingFolderOpen(!isSelectingFolderOpen)}
                isMoving={false}
                onSelect={(selectedFolder) => setCurrentFolderId(selectedFolder)}
            />
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    change: (path, value) => dispatch(change(path, value)),
    resetForm: (formId) => dispatch(resetForm(formId)),
    clearModelData: () => dispatch(clearModelData()),
    clearDrawingData: () => dispatch(clearDrawingData()),
    resetResults: () => dispatch(resetResults()),
    clearUpgradeData: () => dispatch(clearUpgradeData()),
    setExportErrors: () => dispatch(setExportErrors()),
    modelLoading: () => dispatch(modelLoading()),
    fetchModelData: (modelId) => dispatch(fetchModelData(modelId)),
    fetchUserDir: (uid) => dispatch(fetchUserDir(uid)),
    createModel: (modelData, uid, parentFolderId) => dispatch(createModel(modelData, uid, parentFolderId)),
});

export default connect(null, mapDispatchToProps)(Basic);
